import { ComponentType, HTMLAttributes, ReactNode, useEffect, useState } from 'react';

import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type Row as TanstackRow,
  type Table as TanstackTable,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';

import { TableResponsiveWrapper } from '../../patterns/table/table-responsive-wrapper';
import { cn } from '../../utils/cn';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '../context-menu';
import { Skeleton } from '../skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table';
import { COLUMN_CHECKBOX } from './constants/column-checkbox';
import { DataTablePagination } from './data-table-pagination';
import { DataTableToolbar } from './data-table-toolbar';

export interface DataTableProps<TData, TValue> extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'id'
> {
  columns:
    | {
        enableSelection?: boolean;
        schema: ColumnDef<TData, TValue>[];
      }
    | ColumnDef<TData, TValue>[];
  data: TData[];
  filtering?: {
    enable?: boolean;
    placeholder?: string;
    queryParam?: string;
  };
  isLoading?: boolean;
  pagination?: {
    enable?: boolean;
    pageNumberOfOptions?: number;
    pageSize?: number;
  };
  rowContextMenuContent?: (row: TanstackRow<TData>) => ReactNode;
  toolbar?: ComponentType<{ table: TanstackTable<any> }>;
  visibility?: {
    initialState?: Record<string, boolean>;
  };
}

type DataTableColumnMeta = {
  cellClassName?: string;
  headerClassName?: string;
};

export function DataTable<TData, TValue>({
  className,
  columns,
  data,
  filtering,
  id,
  isLoading = false,
  pagination,
  rowContextMenuContent,
  toolbar,
  visibility,
}: DataTableProps<TData, TValue>) {
  const initPagination = { enable: true, ...pagination };
  const initFiltering = { enable: true, ...filtering };

  const Toolbar = toolbar;
  const schema = 'schema' in columns ? columns.schema : columns;
  const enableSelection = 'enableSelection' in columns ? columns?.enableSelection : false;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    visibility?.initialState || {},
  );
  const [globalFilter, setGlobalFilter] = useState<any>([]);
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    columns: enableSelection ? COLUMN_CHECKBOX.concat(schema) : schema,
    data,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    state: {
      columnFilters,
      columnVisibility,
      globalFilter,
      rowSelection,
      sorting,
    },
  });

  // Initialize paginationSize model
  useEffect(() => {
    if (initPagination?.pageSize) table.setPageSize(initPagination?.pageSize);
  }, [initPagination?.pageSize, table]);

  return (
    <div className="grid gap-4">
      {initFiltering?.enable && (
        <DataTableToolbar disabled={isLoading} filtering={initFiltering} id={id} table={table} />
      )}
      {Toolbar ? <Toolbar table={table} /> : null}
      <TableResponsiveWrapper data={data}>
        <Table id={id ? `${id}-table` : undefined} className={cn('mb-3', className)}>
          <TableHeader className="select-none">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow id={id ? `${id}-table-headerRow` : undefined} key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnMeta = header.column.columnDef.meta as
                    DataTableColumnMeta | undefined;

                  return (
                    <TableHead
                      className={cn(columnMeta?.headerClassName)}
                      id={id ? `${id}-table-headerCol-${header.id}` : undefined}
                      key={header.id}
                      colSpan={header.colSpan}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <LoadingState columnsLength={table.getHeaderGroups()[0].headers.length} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <ContextMenu key={row.id}>
                    <ContextMenuTrigger asChild>
                      <TableRow
                        id={
                          id
                            ? `${id}-table-BodyRow-${row?.original?.id ? row.original.id : row.id}`
                            : undefined
                        }
                        data-state={row.getIsSelected() && 'selected'}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const columnMeta = cell.column.columnDef.meta as
                            DataTableColumnMeta | undefined;

                          return (
                            <TableCell
                              className={cn(columnMeta?.cellClassName)}
                              id={
                                id
                                  ? `${id}-table-BodyCol-${row?.original?.id ? `${row.original.id}-${cell.column.id}` : `${row.id}-${cell.id}`}`
                                  : undefined
                              }
                              key={cell.id}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </ContextMenuTrigger>
                    {rowContextMenuContent ? (
                      <ContextMenuContent>{rowContextMenuContent(row)}</ContextMenuContent>
                    ) : null}
                  </ContextMenu>
                );
              })
            ) : (
              <EmptyState columnsLength={table.getHeaderGroups()[0].headers.length} />
            )}
          </TableBody>
        </Table>
      </TableResponsiveWrapper>
      {initPagination?.enable && (
        <DataTablePagination
          disabled={isLoading}
          id={id}
          table={table}
          pageNumberOfOptions={initPagination?.pageNumberOfOptions}
          pageSize={initPagination?.pageSize}
        />
      )}
    </div>
  );
}

function EmptyState({ columnsLength }: { columnsLength: number }) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columnsLength} className="h-40 text-start">
        <span className="text-muted-foreground select-none">Sin resultados 🫥</span>
      </TableCell>
    </TableRow>
  );
}

function LoadingState({ columnsLength }: { columnsLength: number }) {
  const cols = Array(columnsLength).fill(1);
  return (
    <>
      {[1, 2, 3].map((row) => (
        <TableRow key={row} className="hover:bg-transparent">
          {cols.map((col, index) => (
            <TableCell key={`${col}${index}`}>
              <Skeleton
                className={cn('h-8', {
                  'size-8 rounded-full': index === cols.length - 1,
                  'size-5 rounded-[0.25rem]': index === 0,
                })}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
